import { USER_RELOAD } from '../../actions/actionTypes/trainer';
import {
    DO_LOGIN_FAILED,
    DO_LOGIN_REQUEST,
    LOGOUT,
    SET_LOCALE_FAIL,
    SET_LOCALE_REQUEST,
    SET_USER_SCORE_FAILED,
    SET_USER_SCORES_SUCCESS,
    UPDATE_NICK_NAME_FAIL,
    UPDATE_NICK_NAME_REQUEST,
    UPDATE_NICK_NAME_SUCCESS
} from '../../actions/actionTypes/user';
import { USER_PROFILE_FETCH_FAIL, USER_PROFILE_FETCH_SUCCESS } from '../../actions/actionTypes/userProfile';
import { getValidLocale } from '../../../utils/locale';
import { saveLoginInfo } from '../../../utils/login';
import type { UserProfile, UserScore, UserScoreError, UserState } from '../../types';

export const initialState: UserState = {
    locale: getValidLocale(navigator.language),
    personalUsername: null,
    userScore: null,
    userScoreError: null,
    profile: null,
    authWithPasswordPending: false,
    authWithPasswordError: '',
    pending: true,
    errorCode: null,
    initialized: false
};

interface ReducerAction {
    type: string;
    payload: Record<string, unknown>;
    error?: unknown;
}

interface LocalePayload {
    locale?: string;
}

interface ErrorPayload {
    error?: string;
}

interface LoginRequestPayload {
    locationName?: string;
    username?: string;
    password?: string;
}

interface LoginFailedPayload {
    status?: number;
}

interface UpdateNicknameFailPayload {
    errorCode?: number;
}

function toLocalePayload(payload: Record<string, unknown>): LocalePayload {
    return { locale: typeof payload.locale === 'string' ? payload.locale : undefined };
}

function toErrorPayload(payload: Record<string, unknown>): ErrorPayload {
    return { error: typeof payload.error === 'string' ? payload.error : undefined };
}

function toLoginRequestPayload(payload: Record<string, unknown>): LoginRequestPayload {
    return {
        locationName: typeof payload.locationName === 'string' ? payload.locationName : undefined,
        username: typeof payload.username === 'string' ? payload.username : undefined,
        password: typeof payload.password === 'string' ? payload.password : undefined
    };
}

function toLoginFailedPayload(payload: Record<string, unknown>): LoginFailedPayload {
    return { status: typeof payload.status === 'number' ? payload.status : undefined };
}

function toProfilePayload(payload: Record<string, unknown>): UserProfile {
    const company = typeof payload.company === 'object' && payload.company !== null ? payload.company : null;

    return {
        id: typeof payload.id === 'string' || typeof payload.id === 'number' ? payload.id : undefined,
        languageCode: typeof payload.languageCode === 'string' ? payload.languageCode : undefined,
        personalUsername: typeof payload.personalUsername === 'string' ? payload.personalUsername : null,
        companyUsername: typeof payload.companyUsername === 'string' ? payload.companyUsername : undefined,
        email: typeof payload.email === 'string' ? payload.email : undefined,
        telephonyId: typeof payload.telephonyId === 'string' ? payload.telephonyId : undefined,
        emailVerified: typeof payload.emailVerified === 'boolean' ? payload.emailVerified : undefined,
        secret: typeof payload.secret === 'string' ? payload.secret : undefined,
        company:
            company && typeof (company as { name?: unknown }).name === 'string' ? { name: (company as { name: string }).name } : undefined
    };
}

function toUpdateNicknameFailPayload(payload: Record<string, unknown>): UpdateNicknameFailPayload {
    return { errorCode: typeof payload.errorCode === 'number' ? payload.errorCode : undefined };
}

function toUserScoreError(value: unknown): UserScoreError | null {
    return typeof value === 'object' && value !== null ? (value as UserScoreError) : null;
}

interface UserScorePayload {
    score: number;
    maxScore: number;
    minScore: number;
    rank: number;
}

function toNumber(value: unknown, fallback = 0): number {
    return typeof value === 'number' ? value : fallback;
}

function toUserScorePayload(payload: Record<string, unknown>): UserScorePayload {
    return {
        score: toNumber(payload.score),
        maxScore: toNumber(payload.maxScore),
        minScore: toNumber(payload.minScore),
        rank: toNumber(payload.rank)
    };
}

function setScores(payload: UserScorePayload): UserScore {
    const { score, maxScore, minScore, rank } = payload;
    const scoresInterval = maxScore - minScore;
    const userScoreOffset = score - minScore;
    return {
        rank,
        score,
        percentage: (scoresInterval ? userScoreOffset / scoresInterval : 1) * 100
    };
}

const user = (state: UserState = initialState, action: ReducerAction): UserState => {
    const { type, payload, error } = action;

    switch (type) {
        case SET_LOCALE_REQUEST: {
            return { ...state, locale: String(toLocalePayload(payload).locale || ''), pending: true };
        }
        case SET_LOCALE_FAIL: {
            return { ...state, error: toErrorPayload(payload).error, pending: false };
        }
        case USER_PROFILE_FETCH_FAIL: {
            return initialState;
        }
        case DO_LOGIN_REQUEST: {
            const { locationName, username, password } = toLoginRequestPayload(payload);

            saveLoginInfo(String(locationName || ''), String(username || ''), String(password || ''));

            return {
                ...state,
                authWithPasswordError: '',
                authWithPasswordPending: true
            };
        }
        case DO_LOGIN_FAILED: {
            let message = 'login.UnknownError';
            switch (toLoginFailedPayload(payload).status) {
                case 0: // no network
                    message = 'login.NoNetwork';
                    break;
                case 401: // wrong credentials
                    message = 'login.InvalidCredentials';
                    break;
                default: // skip
            }

            return {
                ...state,
                authWithPasswordError: message,
                authWithPasswordPending: false
            };
        }
        case USER_RELOAD: {
            return state;
        }
        case USER_PROFILE_FETCH_SUCCESS: {
            const profilePayload = toProfilePayload(payload);
            return {
                ...state,
                profile: profilePayload,
                locale: String(profilePayload.languageCode || state.locale),
                personalUsername: profilePayload.personalUsername || null,
                pending: false,
                initialized: true
            };
        }
        case SET_USER_SCORES_SUCCESS: {
            const p = toUserScorePayload(payload);
            const hasScoreShape = [p.score, p.maxScore, p.minScore, p.rank].some(v => typeof v === 'number');
            return {
                ...state,
                userScore: hasScoreShape ? setScores(p) : null,
                userScoreError: null
            };
        }
        case SET_USER_SCORE_FAILED: {
            return {
                ...state,
                userScoreError: toUserScoreError(error)
            };
        }

        /*
        UPDATE_NICK_NAME
         */

        case UPDATE_NICK_NAME_REQUEST: {
            const nickName = String(payload.nickName || '');

            return {
                ...state,
                personalUsername: nickName,
                pending: true,
                saved: false
            };
        }

        case UPDATE_NICK_NAME_SUCCESS: {
            return {
                ...state,
                pending: false,
                saved: true,
                errorCode: null
            };
        }

        case UPDATE_NICK_NAME_FAIL: {
            return {
                ...state,
                pending: false,
                enabled: true,

                errorCode: toUpdateNicknameFailPayload(payload).errorCode || null
            };
        }

        case LOGOUT: {
            return state;
        }

        default: {
            return state;
        }
    }
};

export default user;
